import { useCallback, useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { toast } from "react-toastify";
import api from "api/axios";
import Listtable from "components/common/ListTable";
import {
  getSpecialityProcedures,
  setSpecialityProcedureStatus,
  specialityProcedureSheetApi,
} from "api/specialityProcedure";
import { STATUS_OPTIONS } from "config/dentalMasterOptions";
import MasterSheetActions from "components/common/MasterSheetActions";
import {
  importFailures,
  importSkipped,
  importSummary,
} from "components/common/masterSheetReport";
import SpecialityProcedureModal from "./SpecialityProcedureModal";
import {
  PROCEDURE_SHEET_FILES,
  linkedTreatmentsText,
  procedureOptionsText,
  procedureRowLabel,
} from "./specialityProcedureForm";

export const TABLE_HEADERS = [
  "Speciality",
  "Category",
  "Linked Treatments",
  "Procedure Options",
  "Status",
  "Action",
];

// Row keys must not collide with ListTable's special-cased keys ("speciality",
// "name", ...) or with the row's own object fields ("category").
const TABLE_KEYS = [
  "specialityLabel",
  "categoryLabel",
  "linkedTreatmentsLabel",
  "procedureOptionsLabel",
  "statusCell",
  "action",
];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const SpecialityProcedureMaster = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProcedure, setActiveProcedure] = useState({});
  const [specialityOptions, setSpecialityOptions] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [specialityFilter, setSpecialityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const next = searchText.trim();
    if (next === debouncedSearch) return undefined;
    const timer = setTimeout(() => {
      setDebouncedSearch(next);
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, debouncedSearch]);

  useEffect(() => {
    api
      .get("/specialization")
      .then((res) => {
        const list = res?.data?.data?.Specialization;
        setSpecialityOptions(
          (Array.isArray(list) ? list : [])
            .slice()
            .sort((a, b) => String(a?.name).localeCompare(String(b?.name)))
        );
      })
      .catch(() => toast.error("Could not load specialities"));
  }, []);

  // Row buttons are built when the list loads; reload through a ref so a toggle
  // always refetches with the current page and filters.
  const fetchRef = useRef(() => {});

  const handleToggleStatus = useCallback(async (item) => {
    const nextActive = item.isActive === false;
    setBusy(true);
    try {
      const response = await setSpecialityProcedureStatus(item._id, nextActive);
      toast.success(
        response?.data?.message ||
          `Procedure ${nextActive ? "activated" : "deactivated"} successfully`
      );
      fetchRef.current();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Could not ${nextActive ? "activate" : "deactivate"} this procedure.`
      );
    } finally {
      setBusy(false);
    }
  }, []);

  const fetchProcedures = useCallback(() => {
    setLoading(true);

    getSpecialityProcedures({
      page: pageNumber + 1,
      perPage: rowsPerPage,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(specialityFilter ? { speciality: specialityFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => {
            const label = procedureRowLabel(item);
            const inactive = item.isActive === false;
            return {
              ...item,
              specialityLabel: item?.category?.speciality?.name || "-",
              categoryLabel: item?.category?.name || "-",
              linkedTreatmentsLabel: linkedTreatmentsText(item),
              procedureOptionsLabel: procedureOptionsText(item),
              statusCell: (
                <Chip
                  size="small"
                  label={inactive ? "Inactive" : "Active"}
                  color={inactive ? "default" : "success"}
                  variant="outlined"
                />
              ),
              action: (
                <Box sx={{ display: "flex", gap: "0.25rem" }}>
                  <IconButton
                    aria-label={`Edit ${label}`}
                    disabled={item.readOnly === true}
                    onClick={() => {
                      setActiveProcedure(item);
                      setModalOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" color={item.readOnly ? "disabled" : "primary"} />
                  </IconButton>
                  <Tooltip title={inactive ? "Activate" : "Deactivate"}>
                    <span>
                      <IconButton
                        aria-label={`${inactive ? "Activate" : "Deactivate"} ${label}`}
                        disabled={item.readOnly === true}
                        onClick={() => handleToggleStatus(item)}
                      >
                        {inactive ? (
                          <ToggleOffIcon fontSize="small" color="disabled" />
                        ) : (
                          <ToggleOnIcon fontSize="small" color="success" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ),
            };
          })
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Could not load procedures.");
      })
      .finally(() => setLoading(false));
  }, [pageNumber, rowsPerPage, debouncedSearch, specialityFilter, statusFilter, handleToggleStatus]);

  fetchRef.current = fetchProcedures;

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveProcedure({});
    if (reason !== "close") fetchProcedures();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label="Search procedures"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="procedure-speciality-filter-label">Speciality</InputLabel>
          <Select
            labelId="procedure-speciality-filter-label"
            label="Speciality"
            value={specialityFilter}
            onChange={(event) => {
              setSpecialityFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "procedure-speciality-filter" }}
          >
            <MenuItem value="">All</MenuItem>
            {specialityOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140, mr: "auto" }}>
          <InputLabel id="procedure-status-filter-label">Status</InputLabel>
          <Select
            labelId="procedure-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "procedure-status-filter" }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <MasterSheetActions
          api={specialityProcedureSheetApi}
          files={PROCEDURE_SHEET_FILES}
          exportParams={{
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(specialityFilter ? { speciality: specialityFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
          }}
          disabled={busy}
          inputTestId="procedure-master-import-input"
          importSummary={importSummary}
          importFailures={importFailures}
          importSkipped={importSkipped}
          onImported={() => {
            setPageNumber(0);
            fetchRef.current();
          }}
        />

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveProcedure({});
            setModalOpen(true);
          }}
        >
          Add Procedure
        </Button>
      </Box>

      <Listtable
        tableheader={TABLE_HEADERS}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={TABLE_KEYS}
        tag="specialityProcedures"
      />

      {modalOpen && (
        <SpecialityProcedureModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeProcedure}
        />
      )}

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default SpecialityProcedureMaster;
